/**
 * secure-score-brief.ts — Editorial copy layer for the Secure Score page
 * standfirst. Mirrors `posture-brief.ts` in shape and voice.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (percentage, delta, category scores, top recommendation,
 * points-to-gain), never from picking among synonymous phrasings of the
 * same fact.
 *
 * The Secure Score story keys off four signals: percentage of max, 30-day
 * delta (points), weakest of the four Microsoft categories (Identity / Data
 * / Apps / Device), and the top open recommendation. Verdicts are six words
 * or fewer.
 *
 * Verdict precedence at pct >= 90 (non-negative), so the verdict never
 * says "Routine watch only" beside a headline that names an open item:
 * laggard -> topRec open -> points-to-gain known and positive -> routine
 * watch.
 *
 * Loading-state handling: `score == null` short-circuits to the neutral
 * "Secure Score is loading" brief before any of the signals above are read,
 * so the brief never claims a category is "the lowest" or a delta is
 * "up"/"down" while the underlying data hasn't arrived yet.
 *
 * Action-first fields (blueprint §6.2 row 6): `headline` names the top
 * recommendation (sorted by gap, so it is the largest), else the laggard
 * category; `lede` names the percentage and the points still to gain. A
 * category named "Identity" is a Secure Score category, not the Guardian
 * Identity pillar, so its emphasis never takes a pillar ink.
 */

import type { SecureScoreResult } from '@/types/secureScore'
import { briefProvenance, cap } from '@/lib/brief-helpers'
import type { ActionBriefFields } from '@/lib/brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface SecureScoreBrief {
  verdict: string
  /** The provenance line (rule 4, D36): "Synced by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  delta: { value: number; days: number } | null
  /** Action-first h1 (blueprint §6.2 row 6). */
  headline: string
  /** The category the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
}

export type SecureScoreCategoryKey = 'identity' | 'data' | 'apps' | 'device'

const CATEGORY_LABEL: Record<SecureScoreCategoryKey, string> = {
  identity: 'Identity',
  data: 'Data',
  apps: 'Apps',
  device: 'Device',
}

export interface SecureScoreCategorySummary {
  identity?: number
  data?: number
  apps?: number
  device?: number
}

export interface SecureScoreTopRec {
  title: string
  gap: number
  category: string
}

export interface SecureScoreBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** When Guardian last synced Secure Score from Microsoft (`fetchedAt`): null
   *  when never, undefined while unknown. Feeds the provenance line, which says
   *  "Synced" because the data is Microsoft's as is (terminology decision 5). */
  syncedAt?: string | null
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Callers still pass this; unused by the composer now that copy is
   *  deterministic rather than seeded per tenant per day. */
  tenantId?: string
  /** Microsoft Secure Score result. When null we return the loading brief. */
  score?: SecureScoreResult | null
  /** Top open recommendations sorted by gap desc. May be empty. */
  topRecs?: SecureScoreTopRec[]
  /** Per-category percentages (0-100). Undefined when the breakdown hasn't
   *  loaded. */
  categories?: SecureScoreCategorySummary
  /** Total points-to-gain across all open recs. Undefined when unknown. */
  totalPtsToGain?: number
  /** 30-day score delta in points. Null/undefined when unknown. */
  delta30d?: number | null
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Strip trailing sentence punctuation from a recommendation title before
 *  it's re-embedded in a new sentence (avoids "Enforce MFA.. first."). */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

function weakestCategory(
  cats: SecureScoreCategorySummary | undefined,
): { key: SecureScoreCategoryKey; pct: number } | undefined {
  if (!cats) return undefined
  const keys: SecureScoreCategoryKey[] = ['identity', 'data', 'apps', 'device']
  let best: { key: SecureScoreCategoryKey; pct: number } | undefined
  for (const k of keys) {
    const v = cats[k]
    if (typeof v !== 'number') continue
    if (!best || v < best.pct) best = { key: k, pct: v }
  }
  return best
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  PULLING_DOWN: '{cat} is pulling the score down.',
  SCORE_DOWN: 'Score down {n} this month.',
  LIFT_NEXT: 'Lift {cat} next.',
  TOP_NEXT: 'Take the top recommendation next.',
  POINTS_OPEN: '{pts} points still to gain.',
  LIFT_FIRST: 'Lift {cat} first.',
  START_TOP: 'Start with the top recommendation.',
  NEEDS_WORK: '{cat} needs the most work.',
  WORK_LIST: 'Work the recommendations top down.',
}

/**
 * Action-first h1 (blueprint §6.2 row 6): the top recommendation, the
 * laggard category, then the all-clear or routine line (cadence only in the
 * verdict's own all-clear band).
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading Secure Score.',
  TOP_REC: '{title} first.',
  LAGGARD: 'Start with {cat} this week.',
  CLEAR: 'Nothing to close this week.',
  CADENCE: 'Keep the usual review cadence this week.',
  WORK_LIST: 'Work the recommendations top down this week.',
}

/** The lede: the percentage, plus the points still to gain when known and positive. */
const LEDE = {
  LOADING: '**Guardian is reading your Secure Score**.',
  PCT: '**Secure Score is at {pct}%**',
  POINTS_ONE: ', with 1 point still to gain',
  POINTS: ', with {pts} points still to gain',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function pickVerdict(
  pct: number,
  negative: boolean,
  weakest: { key: SecureScoreCategoryKey; pct: number } | undefined,
  laggard: boolean,
  n: string,
  hasTopRec: boolean,
  pts: number | undefined,
): string {
  const catLabel = weakest ? CATEGORY_LABEL[weakest.key] : ''

  if (pct >= 90 && !negative) {
    if (laggard) return VERDICT.LIFT_NEXT.replace('{cat}', catLabel)
    if (hasTopRec) return VERDICT.TOP_NEXT
    if (pts !== undefined && pts > 0) return VERDICT.POINTS_OPEN.replace('{pts}', String(pts))
    return VERDICT.ROUTINE_WATCH
  }

  if (pct >= 75) {
    if (negative) {
      if (laggard) return VERDICT.PULLING_DOWN.replace('{cat}', catLabel)
      return VERDICT.SCORE_DOWN.replace('{n}', n)
    }
    if (laggard) return VERDICT.LIFT_NEXT.replace('{cat}', catLabel)
    return VERDICT.TOP_NEXT
  }

  if (pct >= 60) {
    return laggard ? VERDICT.LIFT_FIRST.replace('{cat}', catLabel) : VERDICT.START_TOP
  }

  return laggard ? VERDICT.NEEDS_WORK.replace('{cat}', catLabel) : VERDICT.WORK_LIST
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 6)                        */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  topRec: SecureScoreTopRec | undefined,
  laggard: boolean,
  weakest: { key: SecureScoreCategoryKey; pct: number } | undefined,
  pts: number | undefined,
  pct: number,
  negative: boolean,
): { headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  if (topRec?.title) {
    return { headline: HEADLINE_ACTION.TOP_REC.replace('{title}', cap(stripTrailingPunct(topRec.title))) }
  }
  if (laggard && weakest) {
    const cat = CATEGORY_LABEL[weakest.key]
    return { headline: HEADLINE_ACTION.LAGGARD.replace('{cat}', cat), emphasis: { text: cat } }
  }
  if (pts === 0 && pct >= 85) return { headline: HEADLINE_ACTION.CLEAR }
  const routine = pct >= 90 && !negative && !(pts !== undefined && pts > 0)
  return { headline: routine ? HEADLINE_ACTION.CADENCE : HEADLINE_ACTION.WORK_LIST }
}

function buildLede(pct: number, pts: number | undefined): string {
  const head = LEDE.PCT.replace('{pct}', String(pct))
  if (pts === undefined || pts <= 0) return `${head}.`
  const shown = formatDelta(pts)
  const clause = shown === '1' ? LEDE.POINTS_ONE : LEDE.POINTS.replace('{pts}', shown)
  return `${head}${clause}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a SecureScoreBrief from the active Secure Score state. All inputs
 * are optional — the function degrades gracefully toward a plausible
 * "no data" brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the summary query hasn't
 * resolved) we return a neutral "loading" brief rather than synthesizing a
 * worst-band verdict — see `posture-brief.ts` for the same guard and its
 * rationale.
 */
export function composeSecureScoreBrief(input: SecureScoreBriefInput = {}): SecureScoreBrief {
  const byline = briefProvenance(input.syncedAt, { verb: 'Synced', now: input.now })

  if (input.score == null) {
    return {
      verdict: 'Reading score…',
      byline,
      delta: null,
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const pct = Math.round(input.score.percentage)
  const weakest = weakestCategory(input.categories)
  const laggard = !!(weakest && weakest.pct < 70)

  const topRec = (input.topRecs ?? [])[0]
  const pts = typeof input.totalPtsToGain === 'number' ? input.totalPtsToGain : undefined

  const deltaKnown = typeof input.delta30d === 'number'
  const deltaValue = deltaKnown ? (input.delta30d as number) : 0
  const negative = deltaKnown && deltaValue < 0
  const n = formatDelta(Math.abs(deltaValue))
  const days = 30

  const verdict = pickVerdict(pct, negative, weakest, laggard, n, !!topRec?.title, pts)

  const action = pickActionHeadline(topRec, laggard, weakest, pts, pct, negative)
  const lede = buildLede(pct, pts)

  return {
    verdict,
    byline,
    delta: deltaKnown ? { value: deltaValue, days } : null,
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
  CATEGORY_LABEL,
}
